// frontend/src/admin/AskAiSettings.js — NEW FILE (Phase E)
// Admin configuration for the student Ask AI feature.
// Everything is OFF by default; nothing shows to students until enabled here.

import React, { useState, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://80.225.197.31:8001';

const BLOCK_TYPES = ['text', 'article', 'table', 'flowchart', 'steps', 'html'];

const AskAiSettings = ({ onBack }) => {
    const [config, setConfig] = useState(null);
    const [subjects, setSubjects] = useState([]);
    const [studentIdsText, setStudentIdsText] = useState('');
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const [cfgRes, subRes] = await Promise.all([
                    fetch(`${API_BASE}/ask-ai/config`),
                    fetch(`${API_BASE}/subjects/`),
                ]);
                if (!cfgRes.ok) throw new Error('Failed to load config');
                const cfg = await cfgRes.json();
                setConfig(cfg);
                setStudentIdsText((cfg.enabled_student_ids || []).join(', '));
                if (subRes.ok) setSubjects(await subRes.json());
            } catch (err) {
                setError(err.message);
            }
        })();
    }, []);

    const toggleSubject = (id) => {
        setConfig((c) => {
            const set = new Set(c.enabled_subject_ids || []);
            set.has(id) ? set.delete(id) : set.add(id);
            return { ...c, enabled_subject_ids: [...set] };
        });
    };

    const toggleBlockType = (bt) => {
        setConfig((c) => {
            const set = new Set(c.enabled_block_types || []);
            set.has(bt) ? set.delete(bt) : set.add(bt);
            return { ...c, enabled_block_types: [...set] };
        });
    };

    const save = async () => {
        setSaving(true);
        setMessage(null);
        setError(null);
        try {
            const enabled_student_ids = studentIdsText
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
                .map(Number)
                .filter((n) => !Number.isNaN(n));
            const payload = { ...config, enabled_student_ids };
            const res = await fetch(`${API_BASE}/ask-ai/config`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail || 'Save failed');
            }
            setMessage('Saved. Changes apply immediately to students.');
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    if (error && !config) return <div style={{ padding: 24, color: '#b91c1c' }}>{error}</div>;
    if (!config) return <div style={{ padding: 24 }}>Loading...</div>;

    const row = { display: 'flex', alignItems: 'center', gap: 8, margin: '6px 0' };
    const section = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 18, marginBottom: 16 };

    return (
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '8px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
                {onBack && <button onClick={onBack}>← Back to Dashboard</button>}
                <h2 style={{ margin: 0 }}>✦ Ask AI Settings</h2>
            </div>

            {error && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: 10, borderRadius: 8, marginBottom: 12 }}>{error}</div>}
            {message && <div style={{ background: '#d1fae5', color: '#065f46', padding: 10, borderRadius: 8, marginBottom: 12 }}>{message}</div>}

            <div style={section}>
                <label style={{ ...row, fontWeight: 700, fontSize: '1.05rem' }}>
                    <input
                        type="checkbox"
                        checked={config.master_enabled}
                        onChange={(e) => setConfig((c) => ({ ...c, master_enabled: e.target.checked }))}
                    />
                    Master switch — Ask AI feature ON
                </label>
                <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: '4px 0 0 24px' }}>
                    When off, no student sees Ask AI anywhere, regardless of the settings below.
                </p>
            </div>

            <div style={section}>
                <h3 style={{ marginTop: 0 }}>Subjects</h3>
                {subjects.length === 0 && <p>No subjects found.</p>}
                {subjects.map((s) => (
                    <label key={s.subject_id} style={row}>
                        <input
                            type="checkbox"
                            checked={(config.enabled_subject_ids || []).includes(s.subject_id)}
                            onChange={() => toggleSubject(s.subject_id)}
                        />
                        {s.name}
                    </label>
                ))}
            </div>

            <div style={section}>
                <h3 style={{ marginTop: 0 }}>Block types showing the Ask AI button</h3>
                {BLOCK_TYPES.map((bt) => (
                    <label key={bt} style={row}>
                        <input
                            type="checkbox"
                            checked={(config.enabled_block_types || []).includes(bt)}
                            onChange={() => toggleBlockType(bt)}
                        />
                        {bt}
                    </label>
                ))}
            </div>

            <div style={section}>
                <h3 style={{ marginTop: 0 }}>Student access</h3>
                <label style={row}>
                    <input
                        type="radio"
                        checked={config.student_access === 'all'}
                        onChange={() => setConfig((c) => ({ ...c, student_access: 'all' }))}
                    />
                    All students (in enabled subjects)
                </label>
                <label style={row}>
                    <input
                        type="radio"
                        checked={config.student_access === 'selected'}
                        onChange={() => setConfig((c) => ({ ...c, student_access: 'selected' }))}
                    />
                    Only selected student IDs
                </label>
                {config.student_access === 'selected' && (
                    <textarea
                        rows={2}
                        style={{ width: '100%', marginTop: 8 }}
                        placeholder="Comma-separated student IDs, e.g. 12, 45, 103"
                        value={studentIdsText}
                        onChange={(e) => setStudentIdsText(e.target.value)}
                    />
                )}
            </div>

            <button onClick={save} disabled={saving} style={{ padding: '10px 26px', fontWeight: 600 }}>
                {saving ? 'Saving...' : 'Save Settings'}
            </button>
        </div>
    );
};

export default AskAiSettings;